import { supabase } from '../../utils/supabase';
import { PlayerInfo, ScoreCalculation } from './types';
import { VictoryType, Faction } from '../../types/stats';
import { statsDataService } from './dataService';
import { statsCache } from '../../utils/statsCache';

export interface GameData {
  playerCount: number;
  winningFaction: Faction;
  victoryType: VictoryType;
  taskResults: (boolean | null)[];
  goodMvp: number | null;
  evilMvp: number | null;
  selfDestruct: number | null;
  wrongKill: number | null;
  bugPlayer: number | null;
  gameDate: string;
  gameNumber: number;
  dojoId: string;
  seasonId: string;
}

export class GameService {
  private clearPlayerStatsCache(dojoId?: number, seasonId?: number) {
    // V619/V620: 清除玩家统计缓存，强制重新加载最新数据
    // V620 修复：必须清除所有可能的缓存 key，因为前端可能在使用不同的筛选条件
    const specificKey = dojoId !== undefined && seasonId !== undefined 
      ? `player_stats:${dojoId}:${seasonId}`
      : null;
    
    // 清除特定道场/赛季的缓存
    if (specificKey) {
      statsCache.remove(specificKey);
      console.log(`[V620] 已清除特定缓存：${specificKey}`);
    }
    
    // 清除全局缓存（all:all）
    statsCache.remove('player_stats:all:all');
    console.log('[V620] 已清除全局缓存：player_stats:all:all');
    
    // V620: 额外清除常见组合的缓存，确保万无一失
    // 这是因为用户可能在不同的道场/赛季筛选之间切换
    const commonKeys = [
      'player_stats:1:1',
      'player_stats:1:2',
      'player_stats:2:1',
      'player_stats:2:2',
    ];
    
    commonKeys.forEach(key => {
      statsCache.remove(key);
    });
    console.log('[V620] 已清除常见组合缓存');
  }

  async saveGameRecord(
    gameData: GameData,
    playerInfos: PlayerInfo[],
    calculatedScores: ScoreCalculation[],
    editingGameId: number | null
  ): Promise<void> {
    try {
      console.log('[V109] 开始保存游戏记录:', { editingGameId });

      const dbGameData: any = {
        player_count: gameData.playerCount,
        winning_faction: gameData.winningFaction,
        victory_type: gameData.victoryType,
        task_results: gameData.taskResults,
        good_mvp_player: gameData.goodMvp,
        evil_mvp_player: gameData.evilMvp,
        self_destruct_player: gameData.selfDestruct,
        wrong_kill_player: gameData.wrongKill,
        bug_player: gameData.bugPlayer,
        game_date: gameData.gameDate,
        dojo_id: gameData.dojoId ? Number(gameData.dojoId) : null,
        season_id: gameData.seasonId ? Number(gameData.seasonId) : null,
        game_number: gameData.gameNumber,
        status: 'valid',
      };

      let gameRecord;

      if (editingGameId) {
        console.log('[V109] 编辑模式：先标记旧记录无效, gameId:', editingGameId);
        await statsDataService.invalidateGameRecords(editingGameId);
        console.log('[V109] 旧记录已标记无效，开始插入新记录');

        const { data, error } = await supabase
          .from('game_records')
          .insert(dbGameData)
          .select()
          .single();

        if (error) {
          console.error('[V109] 插入新记录失败:', error);
          console.error('[V109] 错误详情:', {
            code: error?.code,
            message: error?.message,
            details: error?.details,
            hint: error?.hint,
          });
          throw error;
        }
        gameRecord = data;
        console.log('[V109] 新记录插入成功:', gameRecord.id);
      } else {
        const { data, error } = await supabase
          .from('game_records')
          .insert(dbGameData)
          .select()
          .single();

        if (error) {
          console.error('[V109] 插入记录失败:', error);
          console.error('[V109] 错误详情:', {
            code: error?.code,
            message: error?.message,
            details: error?.details,
            hint: error?.hint,
          });
          throw error;
        }
        gameRecord = data;
      }

      const roundRecords = calculatedScores.map(score => {
        const playerInfo = playerInfos.find(p => p.player_number === score.player_number);
        const missionResult = gameData.taskResults.filter(r => r !== null);
        const successCount = missionResult.filter(r => r === true).length;
        const failCount = missionResult.filter(r => r === false).length;

        // 确定刺客行动详情
        let assassinAttempt = '';
        if (gameData.victoryType === 'assassin_success') {
          assassinAttempt = '刺杀梅林成功';
        } else if (gameData.victoryType === 'assassin_fail') {
          assassinAttempt = '提前刺杀失败';
        }

        return {
          game_record_id: gameRecord.id,
          player_number: score.player_number,
          player_nickname: playerInfo?.player_nickname || `${score.player_number}号`,
          player_role: playerInfo?.player_role || '',
          faction: score.faction,
          team_type: score.faction === 'good' ? '正义' : '邪恶',
          score_change: score.total_score,
          is_mvp: score.is_mvp,
          is_good_mvp: score.is_good_mvp,
          is_evil_mvp: score.is_evil_mvp,
          has_penalty: score.penalty < 0,
          penalty_type: score.penalty < 0 ?
            (score.player_number === gameData.selfDestruct ? 'self_destruct' :
             score.player_number === gameData.wrongKill ? 'wrong_kill' :
             score.player_number === gameData.bugPlayer ? 'bug' : null) : null,
          penalty_score: score.penalty,
          mission_result: `${successCount}胜${failCount}败`,
          assassin_attempt: assassinAttempt,
          status: 'valid',
        };
      });

      console.log('[V109] 准备插入玩家记录:', roundRecords.length, '条');

      const { error: insertError } = await supabase
        .from('player_round_records')
        .insert(roundRecords);

      if (insertError) {
        console.error('[V109] 插入玩家记录失败:', insertError);
        console.error('[V109] 错误详情:', {
          code: insertError?.code,
          message: insertError?.message,
          details: insertError?.details,
          hint: insertError?.hint,
        });
        throw insertError;
      }

      console.log('[V109] 玩家记录插入成功');
      console.log('[V109] 触发器将自动更新玩家统计数据');

      // V619: 清除缓存，确保下次加载时获取最新数据
      this.clearPlayerStatsCache(
        gameData.dojoId ? Number(gameData.dojoId) : undefined,
        gameData.seasonId ? Number(gameData.seasonId) : undefined
      );

      // V658: 服务端触发器会自动更新 data_version_cce0d86f_8f5 表，无需手动更新

      console.log('[V109] 游戏记录保存成功');
    } catch (err) {
      console.error('[V109] 保存游戏记录失败:', err);
      throw err;
    }
  }

  async deleteGameRecord(gameId: number): Promise<void> {
    try {
      console.log(`[V109] 删除游戏记录 ${gameId}`);

      await statsDataService.invalidateGameRecords(gameId);

      console.log(`[V109] 游戏记录 ${gameId} 已标记为无效`);
      console.log(`[V109] 触发器将自动更新玩家统计数据`);
      
      // V619: 清除缓存，确保下次加载时获取最新数据
      this.clearPlayerStatsCache();
      
      // V658: 服务端触发器会自动更新 data_version_cce0d86f_8f5 表，无需手动更新
      
      console.log(`[V109] 删除完成`);
    } catch (err: any) {
      console.error('[V109] 删除游戏记录失败:', err);
      console.error('[V109] 错误详情:', {
        message: err?.message,
        code: err?.code,
        status: err?.status,
        stack: err?.stack,
      });
      throw err;
    }
  }
}

export const gameService = new GameService();
